import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { Shield, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { getMySurvey, submitSurvey } from '../../services/surveyService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
  padding-bottom: 16px;
`;

const Description = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #64748b;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const AnonymousNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #eff6ff;
  border-radius: 6px;
  padding: 12px 20px;
  margin-bottom: 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #2563eb;
`;

const ProgressBarContainer = styled.div`
  background: #E8ECF0;
  border-radius: 4px;
  height: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.primary.main};
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${({ $pct }) => $pct}%;
`;

const ProgressText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #A0A8B4;
  margin-bottom: 8px;
  text-align: right;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const QuestionCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const QuestionText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
`;

const RequiredMark = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $color }) => $color + '15'};
  color: ${({ $color }) => $color};
  margin-bottom: 16px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 6px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ $selected }) => $selected ? '#eff6ff' : '#f8fafc'};
  border: 1.5px solid ${({ $selected }) => $selected ? '#2563eb' : 'transparent'};
  transition: all 0.15s;
  &:hover { background: #eff6ff; }
`;

const RadioDot = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${({ $selected }) => $selected ? '#2563eb' : '#D0D5DD'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ $selected }) => $selected ? '#2563eb' : 'transparent'};
  }
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 6px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ $selected }) => $selected ? '#f0fdf4' : '#f8fafc'};
  border: 1.5px solid ${({ $selected }) => $selected ? '#16a34a' : 'transparent'};
  transition: all 0.15s;
  &:hover { background: #f0fdf4; }
`;

const CheckboxBox = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid ${({ $selected }) => $selected ? '#16a34a' : '#D0D5DD'};
  background: ${({ $selected }) => $selected ? '#16a34a' : '#fff'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CheckMark = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ScoreLabel = styled.span`
  font-size: 11px;
  color: #A0A8B4;
  margin-left: auto;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const NoData = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
  text-align: center;
  padding: 60px 0;
`;

const typeColors = {
  singleChoice: '#2563eb',
  singleChoiceAlternate: '#3b82f6',
  multipleChoice: '#16a34a',
  multipleChoiceAlternate: '#ca8a04',
  ranking: '#dc2626',
  freeText: '#9333ea',
};

const RankItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 4px;
  margin-bottom: 6px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: #fef2f2;
  border: 1.5px solid #fecaca;
  cursor: grab;
`;

const RankNumber = styled.span`
  font-weight: 700;
  color: #dc2626;
  min-width: 20px;
`;

function SortableRankItem({ item, index }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <RankItem ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <GripVertical size={14} style={{ color: '#A0A8B4', flexShrink: 0 }} />
      <RankNumber>{index + 1}.</RankNumber>
      <span>{item.label}</span>
    </RankItem>
  );
}

export default function SurveyFillPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    getMySurvey(id)
      .then((res) => setSurvey(res.data?.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const questions = useMemo(() => survey?.questions || [], [survey]);

  // Initialize ranking-type answers with the question's option order as soon as
  // the survey loads. This MUST run in an effect rather than during render —
  // calling setAnswers from inside a render body used to drop us into a
  // "Too many re-renders" (React #301) infinite loop on any survey containing
  // a ranking question.
  useEffect(() => {
    if (questions.length === 0) return;
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const q of questions) {
        if (q.questionType !== 'ranking') continue;
        if (next[q.id]) continue;
        next[q.id] = (q.options || []).map((o) => ({ id: o.id, label: o.label }));
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [questions]);

  const setAnswer = useCallback((questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const toggleMultiAnswer = useCallback((questionId, optionId, maxSel) => {
    setAnswers((prev) => {
      const current = prev[questionId] || [];
      if (current.includes(optionId)) {
        return { ...prev, [questionId]: current.filter((v) => v !== optionId) };
      }
      const limit = parseInt(maxSel, 10);
      if (!isNaN(limit) && limit > 0 && current.length >= limit) {
        return prev;
      }
      return { ...prev, [questionId]: [...current, optionId] };
    });
  }, []);

  const answeredCount = questions.filter((q) => {
    const a = answers[q.id];
    if (q.questionType === 'freeText') return a && a.trim();
    if (q.questionType === 'singleChoice' || q.questionType === 'singleChoiceAlternate') return a !== undefined && a !== null;
    if (q.questionType === 'multipleChoice' || q.questionType === 'multipleChoiceAlternate') return a && a.length > 0;
    if (q.questionType === 'ranking') return a && a.length > 0;
    return false;
  }).length;

  const progressPct = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const handleRankDragEnd = useCallback((questionId) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setAnswers((prev) => {
      const current = prev[questionId] || [];
      const oldIndex = current.findIndex((item) => item.id === active.id);
      const newIndex = current.findIndex((item) => item.id === over.id);
      return { ...prev, [questionId]: arrayMove(current, oldIndex, newIndex) };
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    const payload = questions.map((q) => {
      const a = answers[q.id];
      if (q.questionType === 'singleChoice') return { questionId: q.id, selectedOptionId: a };
      if (q.questionType === 'singleChoiceAlternate') {
        return {
          questionId: q.id,
          selectedOptionId: a,
          alternateText: (answers[`${q.id}_other`] || '').trim() || undefined,
        };
      }
      if (q.questionType === 'multipleChoice') return { questionId: q.id, selectedOptionIds: a || [] };
      if (q.questionType === 'multipleChoiceAlternate') {
        return {
          questionId: q.id,
          selectedOptionIds: (a || []).filter((v) => typeof v !== 'string' || !v.startsWith('other:')),
          alternateText: (answers[`${q.id}_other`] || '').trim() || undefined,
        };
      }
      if (q.questionType === 'ranking') {
        return { questionId: q.id, selectedOptionIds: (a || []).map((item) => item.id) };
      }
      if (q.questionType === 'freeText') return { questionId: q.id, textValue: a || '' };
      return { questionId: q.id };
    });

    setSubmitting(true);
    try {
      await submitSurvey(id, { answers: payload });
      history.push('/my-surveys');
    } catch {
      // error handled by interceptor
    } finally {
      setSubmitting(false);
    }
  }, [id, questions, answers, history]);

  if (loading) return <Container><NoData>...</NoData></Container>;
  if (!survey) return <Container><NoData>{t('survey.noRecords')}</NoData></Container>;

  return (
    <Container>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('survey.mySurveys'), path: '/my-surveys' },
          { label: survey.title },
        ]} />

        {survey.description && (
          <Description>{survey.description}</Description>
        )}

        {survey.isAnonymous && (
          <AnonymousNotice>
            <Shield size={16} />
            {t('survey.anonymousNotice')}
          </AnonymousNotice>
        )}

        <ProgressText>{t('survey.progress')}: {answeredCount}/{questions.length} ({progressPct}%)</ProgressText>
        <ProgressBarContainer>
          <ProgressFill $pct={progressPct} />
        </ProgressBarContainer>
      </StickyHeader>

      <ScrollArea>
        {questions.map((q, idx) => (
          <div key={q.id}>
            {q.sectionTitle && (idx === 0 || questions[idx - 1]?.sectionTitle !== q.sectionTitle) && (
              <div style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 16, fontWeight: 700, color: '#004242', padding: '16px 0 8px', borderBottom: '2px solid #0291EF', marginBottom: 12 }}>
                {q.sectionTitle}
              </div>
            )}
          <QuestionCard>
            <QuestionText>
              {idx + 1}. {q.questionText}
              {q.isRequired && <RequiredMark>*</RequiredMark>}
            </QuestionText>
            <TypeBadge $color={typeColors[q.questionType]}>{t(`survey.${q.questionType}`)}</TypeBadge>

            {(q.questionType === 'singleChoice' || q.questionType === 'singleChoiceAlternate') && (
              <div>
                {(q.options || []).map((opt) => (
                  <RadioOption
                    key={opt.id}
                    $selected={answers[q.id] === opt.id}
                    onClick={() => setAnswer(q.id, opt.id)}
                  >
                    <RadioDot $selected={answers[q.id] === opt.id} />
                    <span>{opt.label}</span>
                    {opt.score !== undefined && opt.score !== 0 && (
                      <ScoreLabel>({opt.score})</ScoreLabel>
                    )}
                  </RadioOption>
                ))}
                {q.questionType === 'singleChoiceAlternate' && (
                  <div style={{ marginTop: 8 }}>
                    <TextInput
                      label={t('survey.other')}
                      value={answers[`${q.id}_other`] || ''}
                      onChange={(e) => setAnswers((prev) => ({ ...prev, [`${q.id}_other`]: e.target.value }))}
                    />
                  </div>
                )}
              </div>
            )}

            {(q.questionType === 'multipleChoice' || q.questionType === 'multipleChoiceAlternate') && (
              <div>
                {q.maxSelections > 0 && (
                  <div style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 12, color: '#A0A8B4', marginBottom: 8 }}>
                    {t('survey.maxSelectionsHint', { count: q.maxSelections })}
                    {' '} ({(answers[q.id] || []).length}/{q.maxSelections})
                  </div>
                )}
                {(q.options || []).map((opt) => {
                  const selected = (answers[q.id] || []).includes(opt.id);
                  const limit = q.maxSelections ? Number(q.maxSelections) : 0;
                  const atLimit = limit > 0 && (answers[q.id] || []).length >= limit && !selected;
                  return (
                    <CheckboxOption
                      key={opt.id}
                      $selected={selected}
                      onClick={() => toggleMultiAnswer(q.id, opt.id, q.maxSelections)}
                      style={atLimit ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    >
                      <CheckboxBox $selected={selected}>{selected && <CheckMark />}</CheckboxBox>
                      <span>{opt.label}</span>
                    </CheckboxOption>
                  );
                })}
                {q.questionType === 'multipleChoiceAlternate' && (
                  <div style={{ marginTop: 8 }}>
                    <TextInput
                      label={t('survey.other')}
                      value={answers[`${q.id}_other`] || ''}
                      onChange={(e) => setAnswers((prev) => ({ ...prev, [`${q.id}_other`]: e.target.value }))}
                    />
                  </div>
                )}
              </div>
            )}

            {q.questionType === 'ranking' && (() => {
              // Initial order is seeded by the useEffect above; if the effect
              // has not flushed yet (first paint) we fall back to the question's
              // option order so the UI is never empty.
              const items = answers[q.id] || (q.options || []).map((o) => ({ id: o.id, label: o.label }));
              return (
                <div>
                  <div style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 12, color: '#A0A8B4', marginBottom: 8 }}>
                    {t('survey.rankingHint')}
                  </div>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRankDragEnd(q.id)}>
                    <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                      {items.map((item, ri) => (
                        <SortableRankItem key={item.id} item={item} index={ri} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              );
            })()}

            {q.questionType === 'freeText' && (
              <TextInput
                label={t('survey.freeText')}
                value={answers[q.id] || ''}
                onChange={(e) => setAnswer(q.id, e.target.value)}
                textarea
              />
            )}
          </QuestionCard>
          </div>
        ))}

        <Footer>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? '...' : t('survey.submit')}
          </Button>
        </Footer>
      </ScrollArea>
    </Container>
  );
}
